// src/App.jsx
import React, { useEffect } from 'react';
import { Routes, Route, Navigate, Outlet } from 'react-router-dom';
import { useSocket } from './hooks/useSocket';
import { useStore } from './store/useStore';
import { analyticsApi, alertsApi, sourcesApi, logsApi } from './services/api';

import Shell from './components/Shell';
import LandingPage from './components/Landing/LandingPage';
import Dashboard from './components/Dashboard/Dashboard';
import LogsView from './components/Logs/LogsView';
import AlertsView from './components/Alerts/AlertsView';
import AgentView from './components/Agent/AgentView';
import TopologyView from './components/Topology/TopologyView';
import SourceView from './components/Sources/SourceView';
import OrgView from './components/Orgs/OrgView';
import ProjectView from './components/Projects/ProjectView';
import AppView from './components/Apps/AppView';
import DeveloperView from './components/Developers/DeveloperView';
import RBACView from './components/RBAC/RBACView';
import SourcesManagementView from './components/Sources/SourcesManagementView';
import SecurityDashboard from './components/Security/SecurityDashboard';
import AgentObsView from './components/AgentObs/AgentObsView';
import AgentCoreView from './components/AgentCore/AgentCoreView';
import AgentConnectorsView from './components/AgentConnectors/AgentConnectorsView';
import RootCauseView from './components/RootCause/RootCauseView';
import IntelligenceHubView from './components/IntelHub/IntelligenceHubView';
import AgentInventoryView from './components/AgentInventory/AgentInventoryView';

// Layout wrapper — renders Shell around nested <Outlet>
function AppShell() {
  return (
    <Shell>
      <Outlet />
    </Shell>
  );
}

export default function App() {
  useSocket();
  const { setAnalytics, setAlerts, setSources, addLogs } = useStore();

  useEffect(() => {
    async function loadInitialData() {
      try {
        const [analytics, alerts, sources, logs] = await Promise.allSettled([
          analyticsApi.overview(),
          alertsApi.list({ status: 'OPEN', limit: 50 }),
          sourcesApi.list(),
          logsApi.list({ since: 60, limit: 100 }),
        ]);
        if (analytics.status === 'fulfilled') setAnalytics(analytics.value);
        if (alerts.status   === 'fulfilled') setAlerts(alerts.value);
        if (sources.status  === 'fulfilled') setSources(sources.value);
        if (logs.status     === 'fulfilled') addLogs(logs.value.logs || []);
      } catch {
        console.warn('Initial data load failed — using demo mode');
      }
    }
    loadInitialData();
    const interval = setInterval(loadInitialData, 60000);
    return () => clearInterval(interval);
  }, []);

  return (
    <Routes>
      {/* Landing page — no Shell wrapper */}
      <Route path="/"    element={<LandingPage />} />
      <Route path="/home" element={<LandingPage />} />

      {/* App pages — all wrapped in Shell via layout route */}
      <Route element={<AppShell />}>
        <Route path="/overview"         element={<Dashboard />} />
        <Route path="/logs"             element={<LogsView />} />
        <Route path="/alerts"           element={<AlertsView />} />
        <Route path="/agent"            element={<AgentView />} />
        <Route path="/topology"         element={<TopologyView />} />
        <Route path="/sources/:type"    element={<SourceView />} />
        <Route path="/connections"      element={<SourcesManagementView />} />
        <Route path="/security"         element={<SecurityDashboard />} />
        <Route path="/agent-obs"        element={<AgentObsView />} />
        <Route path="/agent-core"       element={<AgentCoreView />} />
        <Route path="/agent-connectors" element={<AgentConnectorsView />} />
        <Route path="/rca"              element={<RootCauseView />} />
        <Route path="/intel-hub"        element={<IntelligenceHubView />} />
        <Route path="/agent-inventory"  element={<AgentInventoryView />} />
        <Route path="/orgs"             element={<OrgView />} />
        <Route path="/projects"         element={<ProjectView />} />
        <Route path="/apps"             element={<AppView />} />
        <Route path="/developers"       element={<DeveloperView />} />
        <Route path="/rbac"             element={<RBACView />} />
        <Route path="*"                 element={<Navigate to="/overview" replace />} />
      </Route>
    </Routes>
  );
}
